import { Button } from "@/components/ui/button";
import { ArrowRight, AlertTriangle, TrendingDown, Check } from "lucide-react";
import { Link } from "react-router-dom";

const LandingJourCritique = () => {
  return (
    <section className="relative py-24 lg:py-32 bg-[#0A0A0F] overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#EF4444]/5 rounded-full blur-[200px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content left */}
          <div className="space-y-8 max-w-xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 rounded-full border border-[#EF4444]/20 bg-[#EF4444]/5 px-4 py-2 animate-fade-up">
              <AlertTriangle className="h-4 w-4 text-[#EF4444]" />
              <span className="text-sm font-medium text-[#EF4444]">
                Fonctionnalité signature
              </span>
            </div>

            {/* Heading */}
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight animate-fade-up [animation-delay:100ms]">
              Savez-vous quand votre chantier
              <span className="block text-[#EF4444]">
                bascule dans le rouge ?
              </span>
            </h2>

            {/* Description */}
            <p className="text-lg text-white/50 leading-relaxed animate-fade-up [animation-delay:200ms]">
              Votre chantier a encore{" "}
              <span className="text-white font-medium">12 jours de marge</span>.
              Au-delà, vous perdez de l'argent. B8ild calcule ce{" "}
              <span className="text-[#F59E0B] font-semibold">Jour Critique</span>{" "}
              et vous alerte à temps.
            </p>

            {/* Features list */}
            <ul className="space-y-4 animate-fade-up [animation-delay:300ms]">
              {[
                "Calcul basé sur vos coûts réels",
                "Alertes 7, 3 et 1 jour avant",
                "Simulation d'impact instantanée",
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="h-5 w-5 rounded-[4px] bg-[#131318] border border-white/[0.06] flex items-center justify-center flex-shrink-0">
                    <Check className="h-3 w-3 text-white/50" strokeWidth={2.5} />
                  </div>
                  <span className="text-white/60">{item}</span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <div className="animate-fade-up [animation-delay:400ms]">
              <Button
                size="lg"
                className="h-12 px-6 bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F] font-semibold text-base gap-2.5 rounded-xl shadow-[0_0_0_1px_rgba(245,158,11,0.3),0_4px_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_0_1px_rgba(245,158,11,0.5),0_8px_30px_rgba(245,158,11,0.4)] hover:scale-[1.02] transition-all duration-300"
                asChild
              >
                <Link to="/auth?mode=signup">
                  Essayer gratuitement
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>

          {/* Chart right */}
          <div className="relative animate-fade-up [animation-delay:200ms] lg:animate-slide-in-right">
            {/* Card with gradient border */}
            <div className="relative rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.1] to-transparent">
              <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl p-6 lg:p-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-white/30 mb-1">Chantier</p>
                    <p className="text-lg font-semibold text-white">Villa Martin</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wider text-white/30 mb-1">Jour critique</p>
                    <p className="text-2xl font-bold text-[#EF4444]">J+12</p>
                  </div>
                </div>

                {/* Chart */}
                <div className="relative h-48 lg:h-56 mb-6">
                  <svg viewBox="0 0 400 180" className="w-full h-full" preserveAspectRatio="none">
                    {/* Grid lines */}
                    {[45, 90, 135].map((y) => (
                      <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="rgba(255,255,255,0.03)" strokeWidth="1" />
                    ))}

                    {/* Zero line */}
                    <line x1="0" y1="90" x2="400" y2="90" stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray="4,4" />

                    {/* Area gradient */}
                    <defs>
                      <linearGradient id="areaGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#10B981" stopOpacity="0.2" />
                        <stop offset="60%" stopColor="#F59E0B" stopOpacity="0.1" />
                        <stop offset="100%" stopColor="#EF4444" stopOpacity="0.1" />
                      </linearGradient>
                      <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#10B981" />
                        <stop offset="60%" stopColor="#F59E0B" />
                        <stop offset="100%" stopColor="#EF4444" />
                      </linearGradient>
                    </defs>

                    {/* Area fill */}
                    <path
                      d="M 0 140 Q 80 135, 150 120 Q 220 105, 280 95 Q 340 80, 400 55 L 400 180 L 0 180 Z"
                      fill="url(#areaGradient)"
                    />

                    {/* Line */}
                    <path
                      d="M 0 140 Q 80 135, 150 120 Q 220 105, 280 95 Q 340 80, 400 55"
                      fill="none"
                      stroke="url(#lineGradient)"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />

                    {/* Critical point */}
                    <circle cx="280" cy="95" r="6" fill="#0F0F14" stroke="#EF4444" strokeWidth="3" />
                    <circle cx="280" cy="95" r="12" fill="none" stroke="#EF4444" strokeWidth="1" opacity="0.3" className="animate-ping" />
                  </svg>

                  {/* Critical day label */}
                  <div className="absolute right-[22%] top-[45%]">
                    <div className="px-3 py-1.5 rounded-lg bg-[#EF4444] text-white text-xs font-semibold shadow-lg">
                      Jour Critique
                    </div>
                  </div>

                  {/* Y axis labels */}
                  <div className="absolute -left-1 top-0 h-full flex flex-col justify-between text-[10px] text-white/30">
                    <span>+30%</span>
                    <span>0%</span>
                    <span>-30%</span>
                  </div>
                </div>

                {/* Footer stats */}
                <div className="flex items-center justify-between pt-4 border-t border-white/[0.05]">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="h-4 w-4 text-[#EF4444]" />
                    <span className="text-sm text-white/40">
                      <span className="text-[#EF4444] font-medium">-2.3%</span>/jour
                    </span>
                  </div>
                  <div className="text-sm text-white/40">
                    Marge: <span className="text-[#F59E0B] font-medium">+8.5%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating alert badge */}
            <div className="absolute -bottom-4 -right-4 rounded-xl p-[1px] bg-gradient-to-br from-[#EF4444]/50 to-transparent animate-float">
              <div className="rounded-xl bg-[#0F0F14]/95 backdrop-blur-xl p-4">
                <AlertTriangle className="h-6 w-6 text-[#EF4444]" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LandingJourCritique;
